import { clientLogger } from '@/lib/clientLogger';
/**
 * Location Tracking Service
 * 
 * Manages background location tracking using transistorsoft/capacitor-background-geolocation.
 * Sends location updates to the Presence API using native HTTP (no JS execution).
 */

import BackgroundGeolocation, {
    Config,
    Location,
    HttpEvent,
} from '@transistorsoft/capacitor-background-geolocation';
import { App } from '@capacitor/app';
import { LocalNotifications } from '@capacitor/local-notifications';
import { Dialog } from '@capacitor/dialog';
import { ENV } from '@/config/environment';
import { isNative, isAndroid, getStorageItem, setStorageItem, requestPermissionExclusive } from './capacitor';
import { initializeDeviceToken, getStoredDeviceToken, clearDeviceToken, requestDeviceToken } from './deviceTokenService';
import { authService } from './firebase';
import { fetchCurrentUserRole } from './userRole';

// Dev-only: expose the SDK for Safari Web Inspector debugging (getState, changePace, logs)
if (ENV.IS_DEVELOPMENT) (window as any).bgGeo = BackgroundGeolocation;

// One-shot fix options. samples:1 returned the first fix CoreLocation produced, which after
// the GPS was powered down (stationary) is often a cell/cached fix worth several km. The
// plugin stops early once a sample meets desiredAccuracy (m), otherwise returns the best one.
const ONE_SHOT_FIX = { samples: 3, desiredAccuracy: 50, maximumAge: 0, timeout: 30, persist: true };

// Track initialization state
let isInitialized = false;
let isTracking = false;

// Mutex to prevent concurrent initialization
let initializationInFlight: Promise<boolean> | null = null;

// Track whether app lifecycle listener is registered
let appLifecycleListenerRegistered = false;

/**
 * Get the current tracking state
 */
export function getTrackingState(): { isInitialized: boolean; isTracking: boolean } {
    return { isInitialized, isTracking };
}

async function dumpState(tag: string) {
    try {
        const state = await BackgroundGeolocation.getState();
        clientLogger.info(`[Location][State][${tag}]`, {
            enabled: state.enabled,
            isMoving: state.isMoving,
            // these may or may not exist depending on platform/plugin version:
            odometer: (state as any).odometer,
            heartbeatInterval: (state as any).heartbeatInterval,
            preventSuspend: (state as any).preventSuspend,
            stopTimeout: (state as any).stopTimeout,
            foregroundService: (state as any).foregroundService,
        });
    } catch (e) {
        clientLogger.debug(`[Location][State][${tag}] failed`, e);
    }
}


/**
 * Build the plugin configuration
 */
function buildConfig(deviceToken: string): Config {
    const locationUrl = `${ENV.API.BASE_URL}/location/update`;

    clientLogger.info('[Location] Configuring with URL:', locationUrl);

    return {
        // License (empty for debug mode with watermark)
        // TODO: Add production license key
        ...(ENV.BG_GEO_LICENSE_KEY ? { license: ENV.BG_GEO_LICENSE_KEY } : {}),

        backgroundPermissionRationale: {
            title: "Allow background location access",
            message:
                "Service Strike collects your location including "+
                "when the app is closed or not in use, so jobs can be " +
                "assigned and monitored. To enable this, set location access to " +
                '"Allow all the time".',
            positiveAction: 'Change to "Allow all the time"',
            negativeAction: "Cancel"
        },

        notification: {
            title: "Location active",
            text: "Location updates are active",
            channelName: "Location Tracking",
            priority: BackgroundGeolocation.NOTIFICATION_PRIORITY_LOW,
            sticky: true
        },

        // Debug settings - only enable in development
        debug: false,
        logLevel: ENV.IS_DEVELOPMENT
            ? BackgroundGeolocation.LOG_LEVEL_VERBOSE
            : BackgroundGeolocation.LOG_LEVEL_WARNING,

        // Inexact scheduling only — avoids the Play-restricted SCHEDULE_EXACT_ALARM
        // permission. Heartbeat/inexact timing is sufficient for presence updates.
        scheduleUseAlarmManager: false,

        // Location accuracy
        desiredAccuracy: BackgroundGeolocation.DESIRED_ACCURACY_HIGH,
        // 0 = purely time-based updates (locationUpdateInterval), not distance-gated.
        // Guarantees a fresh fix on a cadence even when the technician is barely moving
        // (slow traffic, stop lights), which is what "never 10 min silent" needs.
        distanceFilter: 0,

        // Heartbeat fires while stationary to guarantee updates. When the JS process is
        // alive the onHeartbeat handler responds; when it's been killed the native
        // BackgroundGeolocationHeadlessTask responds. Both call getCurrentPosition.
        heartbeatInterval: ENV.LOCATION.HEARTBEAT_INTERVAL_SEC || 240,
        // iOS-only: keep the app awake while stationary so onHeartbeat fires and posts
        // fixes when parked. No effect on Android (foregroundService already does this).
        preventSuspend: true,
        allowIdenticalLocations: true, // Send all locations even if unchanged

        // Android-specific timing while moving: aim for a fix every 5 min, but accept
        // no faster than every 3 min (the floor that caps battery-hungry over-delivery).
        locationUpdateInterval: ENV.LOCATION.UPDATE_INTERVAL_MS || 300000,
        fastestLocationUpdateInterval: 180000,

        // Let the SDK detect stationary and power down GPS (battery) — heartbeat keeps
        // updates flowing while stopped. Disabling stop-detection pins GPS on 24/7, which
        // drains battery and makes OEM battery managers more likely to kill the service.
        disableStopDetection: false,
        stopTimeout: 5,

        // Background operation
        stopOnTerminate: false,      // Keep tracking after app termination
        startOnBoot: true,           // Resume tracking on device boot
        foregroundService: true,     // Required for Android background operation
        enableHeadless: true,        // Native HTTP without JS execution

        // Notification settings (Android)


        // HTTP configuration for native sync
        url: locationUrl,
        method: 'POST',
        headers: {
            'Authorization': `Presence ${deviceToken}`,
            'Content-Type': 'application/json',
        },

        // Don't persist locations if HTTP fails (they become stale quickly)
        maxRecordsToPersist: 5,

        // POST each fix immediately over native HTTP (no batching).
        autoSync: true,
        autoSyncThreshold: 0,
        
    };
}

/**
 * Initialize the background geolocation plugin.
 * Must be called with a valid device token.
 */
export async function initializeLocationTracking(deviceToken: string): Promise<boolean> {
    if (!isNative()) {
        clientLogger.info('[Location] Skipping - not on native platform');
        return false;
    }

    if (isInitialized) {
        clientLogger.info('[Location] Already initialized, updating config');
        await updateDeviceToken(deviceToken);
        return true;
    }

    try {
        clientLogger.info('[Location] Initializing background geolocation');

        const config = buildConfig(deviceToken);

        // Add event listeners before ready()
        setupEventListeners();

        // Initialize the plugin (may use cached state from previous runs)
        const state = await BackgroundGeolocation.ready(config);

        // Force-apply config to override any cached plugin settings
        // This ensures new interval settings take effect without clearing app data
        await BackgroundGeolocation.setConfig(config);
        clientLogger.info('[Location] Config force-applied to override cached state');

        isInitialized = true;
        isTracking = state.enabled;

        clientLogger.info('[Location] Initialized, enabled:', state.enabled);
        await dumpState('after-ready');

        return true;
    } catch (error) {
        clientLogger.debug('[Location] Failed to initialize:', error);
        return false;
    }
}

/**
 * Set up event listeners for the plugin
 */
let listenersReady = false;

function setupEventListeners(): void {
    if (listenersReady) return;
    listenersReady = true;

    // Location event
    BackgroundGeolocation.onLocation((location: Location) => {
        clientLogger.info('[Location] Update:', {
            lat: location.coords.latitude.toFixed(6),
            lng: location.coords.longitude.toFixed(6),
            accuracy: location.coords.accuracy,
        });
    });

    // HTTP response event
    BackgroundGeolocation.onHttp((event: HttpEvent) => {
        clientLogger.info('[Location] HTTP:', event.status, event.success ? 'OK' : 'FAILED');

        if (event.status === 401) {
            clientLogger.debug('[Location] Token rejected (401), refreshing device token');
            // Only fires when the JS process is alive (foreground / app running). When
            // headless, native HTTP has no JS context to refresh — the reduced token
            // rotation (initializeDeviceToken reuses the stored token) is what keeps the
            // background token valid there.
            void refreshDeviceTokenOnAuthFailure();
        }
    });

    BackgroundGeolocation.onHeartbeat(async (event) => {
        clientLogger.info('[Location] HEARTBEAT FIRED at', new Date().toISOString(), 'event:', event);
        void dumpState('heartbeat');

        try {
            const loc = await BackgroundGeolocation.getCurrentPosition(ONE_SHOT_FIX);

            clientLogger.info('[Location] Heartbeat getCurrentPosition OK:', {
                lat: loc.coords.latitude,
                lng: loc.coords.longitude,
                accuracy: loc.coords.accuracy,
            });
        } catch (e) {
            clientLogger.debug('[Location] Heartbeat getCurrentPosition FAILED:', e);
        }
    });



    ///////////////////////////////////////////////////////////////

    BackgroundGeolocation.onMotionChange((event: any) => {
        clientLogger.info('[Location] MotionChange:', event);
        void dumpState('motionChange');
    });

    BackgroundGeolocation.onActivityChange((event: any) => {
        clientLogger.info('[Location] ActivityChange:', event);
    });

    BackgroundGeolocation.onPowerSaveChange((event: any) => {
        clientLogger.info('[Location] PowerSaveChange:', event);
    });

    BackgroundGeolocation.onConnectivityChange((event: any) => {
        clientLogger.info('[Location] ConnectivityChange:', event);
    });
    /////////////////////////////////////////////////////////////////


    // Provider change (GPS enabled/disabled)
    BackgroundGeolocation.onProviderChange((event) => {
        clientLogger.info('[Location] Provider changed:', event);
        void ensurePreciseLocation(event.accuracyAuthorization);
    });

    // Authorization status change
    BackgroundGeolocation.onAuthorization((event) => {
        clientLogger.info('[Location] Authorization:', event);
        if (event.status === BackgroundGeolocation.AUTHORIZATION_STATUS_DENIED) {
            clientLogger.debug('[Location] Location permission denied');
        }
    });
}

/**
 * Update the device token in the plugin configuration.
 * Call this after obtaining a fresh token.
 */
export async function updateDeviceToken(deviceToken: string): Promise<void> {
    if (!isNative() || !isInitialized) {
        return;
    }

    clientLogger.info('[Location] Updating device token');

    await BackgroundGeolocation.setConfig({
        headers: {
            'Authorization': `Presence ${deviceToken}`,
            'Content-Type': 'application/json',
        },
    });
}

// Debounce 401-triggered token refreshes so a burst of failed POSTs can't spin
// up a storm of token requests (each one revokes the previous server-side).
let lastTokenRefreshAt = 0;

async function refreshDeviceTokenOnAuthFailure(): Promise<void> {
    const now = Date.now();
    if (now - lastTokenRefreshAt < 2 * 60 * 1000) return;
    lastTokenRefreshAt = now;

    try {
        const token = await requestDeviceToken();
        await updateDeviceToken(token);
        clientLogger.info('[Location] Device token refreshed after 401');
    } catch (error) {
        clientLogger.debug('[Location] Token refresh after 401 failed:', error);
    }
}

/**
 * Start location tracking
 */
export async function startTracking(): Promise<boolean> {
    if (!isNative()) {
        clientLogger.info('[Location] Skipping start - not on native platform');
        return false;
    }

    if (!isInitialized) {
        clientLogger.debug('[Location] Cannot start - not initialized');
        return false;
    }

    // Check actual native service state instead of JS variable
    // (JS state can be stale after app backgrounding/resume)
    try {
        const currentState = await BackgroundGeolocation.getState();
        if (currentState.enabled) {
            clientLogger.info('[Location] Native service already running');
            isTracking = true;
            return true;
        }
    } catch (e) {
        clientLogger.debug('[Location] Failed to query native state, proceeding with start:', e);
    }

    try {
        clientLogger.info('[Location] Starting tracking');
        const state = await BackgroundGeolocation.start();
        isTracking = state.enabled;
        clientLogger.info('[Location] Started, enabled:', state.enabled);
        await dumpState('after-start');

        // Force an immediate fix. On iOS the SDK starts in the stationary state and
        // won't emit a location until it detects movement, so without this the first
        // update wouldn't arrive until the heartbeat. persist:true → autoSync POSTs it.
        if (state.enabled) {
            void BackgroundGeolocation.getCurrentPosition(ONE_SHOT_FIX)
                .catch((e) => clientLogger.debug('[Location] Initial fix failed:', e));
        }

        return state.enabled;
    } catch (error) {
        clientLogger.debug('[Location] Failed to start:', error);
        return false;
    }
}

/**
 * Stop location tracking
 */
export async function stopTracking(): Promise<void> {
    if (!isNative() || !isInitialized) {
        return;
    }

    try {
        clientLogger.info('[Location] Stopping tracking');
        await BackgroundGeolocation.stop();
        isTracking = false;
        clientLogger.info('[Location] Stopped');
    } catch (error) {
        clientLogger.debug('[Location] Failed to stop:', error);
    }
}

/**
 * Get current location once (for testing/debugging)
 */
export async function getCurrentLocation(): Promise<Location | null> {
    if (!isNative() || !isInitialized) {
        return null;
    }

    try {
        const location = await BackgroundGeolocation.getCurrentPosition(ONE_SHOT_FIX);
        return location;
    } catch (error) {
        clientLogger.debug('[Location] Failed to get current position:', error);
        return null;
    }
}

/**
 * Full initialization: get token and start tracking.
 * Call this on login/app startup.
 * Uses mutex to prevent concurrent initialization.
 */
export async function initializeAndStartTracking(): Promise<boolean> {
    if (!isNative()) {
        clientLogger.info('[Location] Skipping - not on native platform');
        return false;
    }

    // If initialization is already in flight, wait for it.
    // CRITICAL: The mutex must be set SYNCHRONOUSLY (no await between check and set)
    // to prevent multiple onAuthStateChanged callbacks from racing through.
    if (initializationInFlight) {
        clientLogger.info('[Location] Initialization already in flight, waiting...');
        return initializationInFlight;
    }

    // Start initialization and store the promise IMMEDIATELY (before any async work)
    initializationInFlight = doInitializeAndStartTracking().finally(() => {
        initializationInFlight = null;
    });

    return initializationInFlight;
}

// Preferences key recording that the user accepted the location prominent disclosure.
const LOCATION_DISCLOSURE_KEY = 'location_disclosure_accepted_v1';

/**
 * Prominent disclosure for background location collection.
 *
 * Google Play policy requires an in-app disclosure shown BEFORE any location
 * permission request that (1) names the app, (2) explains what location data is
 * collected and why, (3) states that collection continues in the background, and
 * (4) requires affirmative consent. The OS permission prompt and the privacy policy
 * do NOT satisfy this on their own.
 *
 * Returns true if the user has consented (now or previously); false if they declined,
 * in which case tracking must not start.
 */
async function ensureLocationDisclosureAccepted(): Promise<boolean> {
    if (await getStorageItem(LOCATION_DISCLOSURE_KEY) === 'true') {
        return true;
    }

    const { value } = await Dialog.confirm({
        title: 'Location sharing',
        message:
            "Service Strike collects your location including "+
            "when the app is closed or not in use, so jobs can be " +
            'assigned and monitored.\n\nDo you want to allow Service Strike to collect your location?',
        okButtonTitle: 'Allow',
        cancelButtonTitle: 'Not now',
    });

    if (value) {
        await setStorageItem(LOCATION_DISCLOSURE_KEY, 'true');
        return true;
    }

    // TODO: disable app access if user declines
    clientLogger.info('[Location] User declined prominent disclosure — not starting tracking');
    return false;
}

/**
 * Internal initialization logic
 */
async function doInitializeAndStartTracking(): Promise<boolean> {
    try {
        // Only technician-role users should have location tracking
        const currentUser = await authService.getCurrentUser();
        if (!currentUser) {
            clientLogger.info('[Location] Skipping - no authenticated user');
            return false;
        }

        const role = await fetchCurrentUserRole();
        if (role !== 'technician') {
            clientLogger.info('[Location] Skipping - location tracking is only for technicians, current role:', role);
            return false;
        }

        // Prominent disclosure must be shown and accepted before any permission request.
        if (!(await ensureLocationDisclosureAccepted())) {
            return false;
        }

        // Apply the plugin config (which carries backgroundPermissionRationale and
        // locationAuthorizationRequest) BEFORE requesting permission, so the OS
        // "Allow all the time" screen shows our rationale text instead of the
        // transistorsoft [CHANGEME] default. Uses any already-stored token for the HTTP
        // header — this is a local read with no network call, so it can't gate the prompt.
        // The fresh token is fetched and applied below.
        await initializeLocationTracking((await getStoredDeviceToken()) || '');

        // Request the OS location permission IMMEDIATELY after the disclosure — before
        // any backend work. The disclosure above is only the in-app consent; this is what
        // surfaces the actual system prompt. It must not be gated behind the device-token
        // fetch below: if that fails (e.g. backend unreachable), we would return early and
        // the user would see the disclosure but never the OS permission screen.
        const permission = await requestPermissions();
        if (
            permission === BackgroundGeolocation.AUTHORIZATION_STATUS_DENIED ||
            permission === BackgroundGeolocation.AUTHORIZATION_STATUS_RESTRICTED
        ) {
            clientLogger.debug('[Location] Location permission denied — not starting tracking');
            return false;
        }

        // Request notification permission on Android 13+ before starting.
        // The foreground service notification is required to keep the service alive;
        // without it, Android kills the background service.
        if (isAndroid()) {
            await requestNotificationPermission();
            // Battery-optimization exemption prompt intentionally disabled for now
            // (too intrusive). To re-enable, uncomment this and the function below.
            // See docs/location-tracking.md (fix #3).
            // await promptDisableBatteryOptimization();
        }

        // Always get a fresh token on startup, then apply it to the plugin's HTTP header.
        clientLogger.info('[Location] Getting fresh device token');
        const token = await initializeDeviceToken();

        if (!token) {
            clientLogger.debug('[Location] Failed to get device token');
            return false;
        }

        // Re-run init with the fresh token: if the config apply above succeeded this just
        // updates the auth header; if it failed (e.g. ready() threw), this fully initializes.
        const initialized = await initializeLocationTracking(token);
        if (!initialized) {
            return false;
        }

        // Register app lifecycle listener to re-initialize on resume
        setupAppLifecycleListener();

        // Start tracking
        const started = await startTracking();
        if (started) void ensurePreciseLocation();
        return started;
    } catch (error) {
        clientLogger.debug('[Location] Failed to initialize and start:', error);
        return false;
    }
}

// Preferences key holding when we last told the user to turn Precise Location on.
const PRECISE_LOCATION_NAG_KEY = 'location_precise_nag_at';

/**
 * iOS 14+ lets the user grant only approximate location ("Precise Location" off). The OS then
 * reports a position snapped to a multi-km region with ±1–10 km accuracy, and no config
 * can do better. Ask for temporary full accuracy (lasts until the app is terminated), and
 * point the user at Settings for the permanent fix — at most once a day.
 * Android always reports FULL here, so this is a no-op there.
 */
async function ensurePreciseLocation(accuracyAuthorization?: number): Promise<void> {
    try {
        // Both prompts need the foreground; running in the background would also burn the
        // daily nag on a dialog nobody sees. The appStateChange listener retries on resume.
        if (!(await App.getState()).isActive) return;

        accuracyAuthorization ??= (await BackgroundGeolocation.getProviderState()).accuracyAuthorization;
        clientLogger.info('[Location] accuracyAuthorization:', accuracyAuthorization);
        if (accuracyAuthorization !== BackgroundGeolocation.ACCURACY_AUTHORIZATION_REDUCED) return;

        try {
            // Purpose key must exist in Info.plist NSLocationTemporaryUsageDescriptionDictionary.
            const result = await BackgroundGeolocation.requestTemporaryFullAccuracy('Dispatch');
            clientLogger.info('[Location] requestTemporaryFullAccuracy result:', result);
        } catch (e) {
            clientLogger.debug('[Location] requestTemporaryFullAccuracy failed:', e);
        }

        const lastNag = Number(await getStorageItem(PRECISE_LOCATION_NAG_KEY)) || 0;
        if (Date.now() - lastNag < 24 * 60 * 60 * 1000) return;
        await setStorageItem(PRECISE_LOCATION_NAG_KEY, String(Date.now()));

        const { value } = await Dialog.confirm({
            title: 'Turn on Precise Location',
            message:
                'Service Strike only has your approximate location, so dispatchers may see you ' +
                'several miles from where you are.\n\nTap "Open Settings", then Location, and ' +
                'turn on "Precise Location".',
            okButtonTitle: 'Open Settings',
            cancelButtonTitle: 'Not now',
        });
        // iOS can't deep-link past the app's own Settings page. Capacitor hands non-app
        // top-level navigations to UIApplication.open, which resolves app-settings:.
        if (value) window.location.href = 'app-settings:';
    } catch (e) {
        clientLogger.debug('[Location] Precise location check failed:', e);
    }
}

/**
 * Request POST_NOTIFICATIONS permission on Android 13+ (API 33).
 * Without notification permission, the foreground service notification is suppressed
 * and Android will kill the background service.
 */
async function requestNotificationPermission(): Promise<void> {
    try {
        const { display } = await LocalNotifications.checkPermissions();
        clientLogger.info('[Location] Notification permission status:', display);

        if (display !== 'granted') {
            clientLogger.info('[Location] Requesting notification permission');
            const { display: result } = await requestPermissionExclusive(() => LocalNotifications.requestPermissions());
            clientLogger.info('[Location] Notification permission result:', result);

            if (result !== 'granted') {
                clientLogger.debug(
                    '[Location] Notification permission denied. ' +
                    'The foreground service may be killed by Android. ' +
                    'Location tracking reliability will be degraded.'
                );
            }
        }
    } catch (error) {
        clientLogger.debug('[Location] Failed to request notification permission:', error);
    }
}

/**
 * Battery-optimization exemption prompt — CURRENTLY DISABLED.
 *
 * OEM battery managers (Samsung, Xiaomi, Huawei, …) kill foreground services
 * after a few hours unless the app is exempted — a common cause of "location
 * suddenly stops for some users after a while". Prompting for the exemption
 * improves background reliability, but the OS settings screen is intrusive, so
 * it's turned off for now.
 *
 * To re-enable: uncomment this function and its call in
 * doInitializeAndStartTracking (search "promptDisableBatteryOptimization").
 * The SDK tracks whether it already showed the screen (`request.seen`), so the
 * user is only prompted once. See docs/location-tracking.md (fix #3).
 */
// async function promptDisableBatteryOptimization(): Promise<void> {
//     try {
//         if (await BackgroundGeolocation.deviceSettings.isIgnoringBatteryOptimizations()) {
//             return;
//         }
//
//         const request = await BackgroundGeolocation.deviceSettings.showIgnoreBatteryOptimizations();
//         if (request.seen) {
//             return; // Already prompted before — don't nag.
//         }
//
//         const { value } = await Dialog.confirm({
//             title: 'Improve location reliability',
//             message:
//                 'To keep sharing your location reliably in the background, please allow ' +
//                 'Service Strike to run without battery restrictions on the next screen.',
//             okButtonTitle: 'Open settings',
//             cancelButtonTitle: 'Not now',
//         });
//
//         if (value) {
//             await BackgroundGeolocation.deviceSettings.show(request);
//         }
//     } catch (error) {
//         // Some devices don't implement this settings screen — non-fatal.
//         clientLogger.debug('[Location] Battery optimization prompt unavailable:', error);
//     }
// }

/**
 * Register a Capacitor App lifecycle listener to re-initialize tracking on resume.
 * When the app comes back from the background, the native service may have been killed
 * by Android. This ensures we detect that and restart tracking.
 */
function setupAppLifecycleListener(): void {
    if (appLifecycleListenerRegistered) return;
    appLifecycleListenerRegistered = true;

    App.addListener('appStateChange', async ({ isActive }) => {
        if (!isActive) return;

        clientLogger.info('[Location] App resumed, checking tracking state');
        try {
            const state = await BackgroundGeolocation.getState();
            if (!state.enabled && isInitialized) {
                clientLogger.info('[Location] Native service was stopped, restarting tracking');
                await initializeAndStartTracking();
            } else {
                clientLogger.info('[Location] Native service still running, enabled:', state.enabled);
                // Init usually ran in a background relaunch (stopOnTerminate:false), where the
                // precise-location prompts can't show — so re-check on every foreground.
                void ensurePreciseLocation();
            }
        } catch (error) {
            clientLogger.debug('[Location] Failed to check state on resume:', error);
        }
    });

    clientLogger.info('[Location] App lifecycle listener registered');
}

/**
 * Clean up on logout: stop tracking and clear token.
 */
export async function cleanupOnLogout(): Promise<void> {
    clientLogger.info('[Location] Cleaning up on logout');
    await stopTracking();
    await clearDeviceToken();
}

/**
 * Request necessary permissions for location tracking.
 * Returns the permission status.
 */
export async function requestPermissions(): Promise<number> {
    if (!isNative()) {
        return -1;
    }

    try {
        // Request "Always" permission for background tracking
        const status = await requestPermissionExclusive(() => BackgroundGeolocation.requestPermission());
        clientLogger.info('[Location] Permission status:', status);
        return status;
    } catch (error) {
        clientLogger.debug('[Location] Failed to request permission:', error);
        return -1;
    }
}